import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFetchWorkspaceSlugAvailabilityQueryFn } from 'app/queries/workspaces/createFetchWorkspaceSlugAvailabilityQueryFn';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import { GetWorkspaceBySlugsResponseDto, WorkspacesService } from 'app/API';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { mergeServerValues } from './helpers/mergeServerValues';
import { Wrapper } from './styles/Wrapper';

type TProps = {
  workspace: GetWorkspaceBySlugsResponseDto;
  onSaved: (slug: string) => void;
};

export const WorkspaceDetailsForm = ({ workspace, onSaved }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { updateMe, organization } = useAuthContext();
  const canEditWS = organization?.permissions?.manageWorkspaces;

  const serverValues = useMemo(
    () => ({
      name: workspace?.name,
      slug: workspace?.slug,
      editorsNotifications: workspace?.editorsNotifications,
      proofreadersNotifications: workspace?.proofreadersNotifications,
      jobSubmissionNotifications: workspace?.jobSubmissionNotifications,
    }),
    [workspace],
  );
  const previousServerValuesRef = useRef(serverValues);
  const [formValues, setFormValues] = useState(serverValues);
  const [queryError, setQueryError] = useState('');

  useEffect(() => {
    const previousServerValues = previousServerValuesRef.current;
    if (previousServerValues === serverValues) {
      return;
    }
    previousServerValuesRef.current = serverValues;
    setFormValues((currentValues) => mergeServerValues(previousServerValues, serverValues, currentValues));
  }, [serverValues]);

  const organizationId = organization?.id ?? '';
  const {
    status: slugStatus,
    isAvailable: isSlugAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug: formValues.slug ?? '',
    currentSlug: workspace?.slug,
    enabled: !!organizationId,
    checkAvailability: async (slug) => {
      const data = await queryClient.fetchQuery({
        queryKey: workspacesKeys.slugAvailability(organizationId, slug),
        queryFn: createFetchWorkspaceSlugAvailabilityQueryFn(call),
        staleTime: 0,
      });
      return !!data?.available;
    },
  });

  const updateWorkspaceMutation = useMutation({
    mutationFn: (values: typeof formValues) =>
      call(
        () =>
          WorkspacesService.updateWorkspace({
            wsid: workspace?.id ?? '',
            requestBody: {
              name: values.name || '',
              slug: values.slug || '',
              editorsNotifications: values.editorsNotifications,
              proofreadersNotifications: values.proofreadersNotifications,
              jobSubmissionNotifications: values.jobSubmissionNotifications,
            },
          }),
        { onError: (message) => setQueryError(message || 'Unable to save the workspace.') },
      ),
    onMutate: () => setQueryError(''),
    onSuccess: async (data, values) => {
      if (data.success) {
        await updateMe();
        onSaved(values.slug || '');
        return;
      }
      if (data.error) {
        setQueryError(data.error);
      }
    },
  });
  const isSubmitting = updateWorkspaceMutation.isPending;

  const handleSubmit = () => {
    updateWorkspaceMutation.mutate(formValues);
  };

  return (
    <Wrapper>
      <div className="leftSide">
        <h3>General</h3>
      </div>

      <div className="rightSide">
        <div className="rightSide-columns">
          <div className="rightSide-first-row">
            <div className="input-wrapper">
              <label htmlFor="name">Workspace name</label>
              <Input
                className="form-input w-full"
                disabled={!canEditWS || isSubmitting}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormValues({
                    ...formValues,
                    name: e.target.value,
                  })
                }
                name="name"
                value={formValues.name}
                label="Workspace name"
              />
            </div>
            <div className="input-wrapper">
              <label htmlFor="slug">Workspace URL</label>
              <SlugField
                className="w-full"
                label="Workspace URL"
                value={formValues.slug ?? ''}
                status={slugStatus}
                onRetry={retrySlugCheck}
                disabled={!canEditWS}
                onChange={(value) => setFormValues({ ...formValues, slug: value })}
              />
            </div>
          </div>
        </div>

        <div className="messages-row-wrapper">
          {queryError ? <div className="error-message">{queryError}</div> : null}
          {workspace?.slug !== formValues.slug ? (
            <div className="info-message">This change will invalidate sent out job sharing links</div>
          ) : null}
        </div>

        {canEditWS ? (
          <div className="checkbox-wrapper">
            <CheckBox
              name="editors-notifications"
              disabled={!canEditWS}
              onChange={() => {
                setFormValues({ ...formValues, editorsNotifications: !formValues.editorsNotifications });
              }}
              checked={formValues.editorsNotifications}
            />
            <label>Enable notifications for Editors</label>
          </div>
        ) : null}

        {canEditWS ? (
          <div className="checkbox-wrapper">
            <CheckBox
              name="proofreaders-notifications"
              disabled={!canEditWS}
              onChange={() => {
                setFormValues({ ...formValues, proofreadersNotifications: !formValues.proofreadersNotifications });
              }}
              checked={formValues.proofreadersNotifications}
            />
            <label>Enable notifications for Proofreaders</label>
          </div>
        ) : null}

        {canEditWS ? (
          <div className="checkbox-wrapper">
            <CheckBox
              name="job-submission-notifications"
              disabled={!canEditWS}
              onChange={() => {
                setFormValues({ ...formValues, jobSubmissionNotifications: !formValues.jobSubmissionNotifications });
              }}
              checked={formValues.jobSubmissionNotifications}
            />
            <label>Enable notification on Job creation</label>
          </div>
        ) : null}

        {canEditWS ? (
          <div className="save-wrapper">
            <BoldButton
              className="w-[130px]"
              disabled={
                !canEditWS ||
                isSubmitting ||
                isSlugUnverified ||
                !isSlugAvailable ||
                !(formValues.slug && formValues.name)
              }
              onClick={() => handleSubmit()}
            >
              Save
            </BoldButton>
          </div>
        ) : null}
      </div>
    </Wrapper>
  );
};
